// Go module detail (ADR-0038 section 12, api.md 7.1): versions with both h1 hashes and the Origin, the go.mod of a
// version, a `go get` snippet, promote to another namespace, and deletes with the ADR-0031 grace period.
import { useState, type FormEvent } from "react";
import type { GoModule, GoVersion, Pending, TenantDetail } from "../api/types";
import { useServices } from "../components/context";
import { Id } from "../components/IdCell";
import { PendingFilter, useGrace, type PendingRow } from "../components/Grace";
import { CodeBlock, ErrorBanner, Section, useAction } from "../components/ui";
import { useLoad } from "../components/useLoad";
import { formatBytes, formatTime } from "../lib/format";
import { compareGoVersions, goGetCommand, goModRetractions, isRetracted, latestGoVersion } from "../lib/go";
import { routeHash } from "../lib/router";

/** Next to the delete buttons (ADR-0038 section 8): a delete cannot reach module caches and go.sum files. */
export const GO_DELETE_NOTE =
  'Consumers that already downloaded a version keep it in their module cache and go.sum. A delete cannot reach them. To ask them to stop using a version, publish a newer version with a "retract" line in go.mod.';

/** The delete dialog text for a whole Go module: the admin always types the module path. */
export function goModuleDeleteTarget(m: { id: string; namespace: string; path: string }) {
  return {
    id: m.id,
    title: `Go module ${m.path} in ${m.namespace}`,
    message:
      "All its versions are deleted too, and builds that need them fail. Version numbers are never used again.",
    typedName: m.path,
    typedLabel: "module path",
    impact: "go/modules" as const,
  };
}

/** Origin of a version as one short line: tag ref and commit. */
export function originText(o: GoVersion["origin"]): string {
  if (!o) return "";
  const parts: string[] = [];
  if (o.Ref) parts.push(o.Ref.replace(/^refs\/tags\//, ""));
  if (o.Hash) parts.push(o.Hash.slice(0, 12));
  if (o.Subdir) parts.push(`in ${o.Subdir}`);
  return parts.join(" ");
}

export function GoModuleDetail({ tenant, moduleId }: { tenant: TenantDetail; moduleId: string }) {
  const { api } = useServices();
  const detail = useLoad(() => api.getGoModule(tenant.slug, moduleId), [tenant.slug, moduleId]);
  const [gone, setGone] = useState(false);
  const back = routeHash({ name: "tenant", tenant: tenant.slug, page: "packages", type: "go" });

  const moduleGrace = useGrace<{ id: string }>({
    tenant,
    onChange: detail.reload,
    ops: {
      key: `go-module:${moduleId}`,
      listPending: async () => ({ items: [] }),
      remove: async (t) => {
        const res = await api.deleteGoModule(tenant.slug, t.id, t.typedName ?? "");
        setGone(true);
        return res;
      },
      restore: async (id) => {
        await api.restoreGoModule(tenant.slug, id);
        setGone(false);
      },
      purge: (id, confirm) => api.purgeGoModule(tenant.slug, id, confirm),
    },
  });

  const mod = detail.data;
  if (gone) {
    return (
      <>
        <Section title="Go module deleted">
          <p className="muted">
            The module is pending deletion. Restore it with Undo, or from "Pending deletion" in{" "}
            <a href={back}>Go modules</a>.
          </p>
        </Section>
        {moduleGrace.ui}
      </>
    );
  }
  if (!mod) {
    return (
      <Section title="Go module">
        <p>
          <a href={back}>All Go modules</a>
        </p>
        <ErrorBanner error={detail.error} />
        {detail.loading && <p className="muted">Loading...</p>}
      </Section>
    );
  }
  return (
    <>
      <ModuleHeader
        tenant={tenant}
        mod={mod}
        back={back}
        onDelete={moduleGrace.isAdmin ? () => moduleGrace.askDelete(goModuleDeleteTarget(mod)) : undefined}
        busy={moduleGrace.busy}
        error={moduleGrace.error}
      />
      <ModuleVersions tenant={tenant} mod={mod} reload={detail.reload} />
      {moduleGrace.ui}
    </>
  );
}

function ModuleHeader({
  tenant,
  mod,
  back,
  onDelete,
  busy,
  error,
}: {
  tenant: TenantDetail;
  mod: GoModule;
  back: string;
  onDelete?: () => void;
  busy: boolean;
  error: unknown;
}) {
  const latest = latestGoVersion(mod.items);
  return (
    <Section
      title={mod.path}
      actions={
        onDelete && (
          <button type="button" className="danger" disabled={busy} onClick={onDelete}>
            Delete module
          </button>
        )
      }
    >
      <p>
        <a href={back}>All Go modules</a>
      </p>
      <ErrorBanner error={error} />
      <dl className="ns-facts">
        <dt>Module path</dt>
        <dd>
          <code data-testid="module-path">{mod.path}</code>
        </dd>
        <dt>Namespace</dt>
        <dd>
          <a
            href={routeHash({
              name: "tenant",
              tenant: tenant.slug,
              page: "connect",
              ns: mod.namespace,
              type: "go",
            })}
          >
            {mod.namespace}
          </a>
        </dd>
        <dt>Latest version</dt>
        <dd className="mono">{latest?.version ?? "none"}</dd>
        <dt>Size</dt>
        <dd>{formatBytes(mod.size_bytes)}</dd>
        <dt>Last publish</dt>
        <dd>{formatTime(mod.last_publish_at)}</dd>
        <dt>ID</dt>
        <dd>
          <Id id={mod.id} />
        </dd>
      </dl>
      <CodeBlock title="Add it to a Go module" code={`${goGetCommand(mod.path, latest?.version)}\n`} />
    </Section>
  );
}

function ModuleVersions({
  tenant,
  mod,
  reload,
}: {
  tenant: TenantDetail;
  mod: GoModule;
  reload: () => void;
}) {
  const { api } = useServices();
  const [shown, setShown] = useState<GoVersion | null>(null);
  const [promoting, setPromoting] = useState<GoVersion | null>(null);
  const grace = useGrace<GoVersion>({
    tenant,
    onChange: reload,
    ops: {
      key: `go-versions:${mod.id}`,
      listPending: async () => ({
        items: (await api.getGoModule(tenant.slug, mod.id, true)).items as Pending<GoVersion>[],
      }),
      remove: (t) => api.deleteGoVersion(tenant.slug, mod.id, t.id),
      restore: async (id) => void (await api.restoreGoVersion(tenant.slug, mod.id, id)),
      purge: (id, confirm) => api.purgeGoVersion(tenant.slug, mod.id, id, confirm),
    },
  });
  const versions = [...mod.items].sort((a, b) => compareGoVersions(b.version, a.version));
  const newest = versions[0];
  // ADR-0038 section 12: "retracted" is read from the go.mod of the newest version.
  const newestMod = useLoad(
    () => (newest ? api.getGoMod(tenant.slug, mod.id, newest.id) : Promise.resolve("")),
    [tenant.slug, mod.id, newest?.id],
  );
  const retractions = goModRetractions(newestMod.data ?? "");

  const pendingRows: PendingRow[] = grace.items.map((v) => ({
    id: v.id,
    title: <span className="mono">{v.version}</span>,
    detail: formatBytes(v.size_bytes),
    deletedBy: v.deleted_by,
    deletedAt: v.deleted_at,
    purgeAfter: v.purge_after,
    purge: {
      id: v.id,
      title: `version ${mod.path}@${v.version}`,
      cascade: "The version number is never used again.",
      confirm: v.version,
      confirmLabel: "version",
    },
  }));

  return (
    <>
      <Section
        title="Versions"
        actions={<PendingFilter view={grace.view} setView={grace.setView} count={grace.count} />}
      >
        {grace.isAdmin && <p className="muted small">{GO_DELETE_NOTE}</p>}
        <ErrorBanner error={grace.error} />
        {grace.view === "pending" ? (
          grace.rowsTable(pendingRows)
        ) : versions.length === 0 ? (
          <p className="muted">No versions.</p>
        ) : (
          <table className="stack data-table" aria-label="Versions">
            <thead>
              <tr>
                <th>Version</th>
                <th>Published</th>
                <th>By</th>
                <th>Size</th>
                <th>Zip h1</th>
                <th>go.mod h1</th>
                <th>Origin</th>
                <th className="actions">Actions</th>
              </tr>
            </thead>
            <tbody>
              {versions.map((v) => (
                <tr key={v.id}>
                  <td className="mono">
                    <strong>{v.version}</strong>{" "}
                    {isRetracted(v.version, retractions) && <span className="tag denied">retracted</span>}
                  </td>
                  <td data-label="Published">{formatTime(v.published_at)}</td>
                  <td data-label="By">{v.published_by ? <Id id={v.published_by} /> : "unknown"}</td>
                  <td data-label="Size">{formatBytes(v.size_bytes)}</td>
                  <td className="mono small" data-label="Zip h1">
                    {v.h1}
                  </td>
                  <td className="mono small" data-label="go.mod h1">
                    {v.mod_h1}
                  </td>
                  <td className="mono small" data-label="Origin" title={v.origin?.Hash ?? undefined}>
                    {originText(v.origin)}
                  </td>
                  <td className="actions" data-label="Actions">
                    <button
                      type="button"
                      className="secondary"
                      aria-label={`go.mod of ${v.version}`}
                      onClick={() => setShown(shown?.id === v.id ? null : v)}
                    >
                      go.mod
                    </button>
                    {grace.isAdmin && (
                      <>
                        <button
                          type="button"
                          className="secondary"
                          aria-label={`Promote ${v.version}`}
                          onClick={() => setPromoting(v)}
                        >
                          Promote
                        </button>
                        <button
                          type="button"
                          className="danger"
                          aria-label={`Delete version ${v.version}`}
                          disabled={grace.busy}
                          onClick={() =>
                            grace.askDelete({
                              id: v.id,
                              title: `version ${mod.path}@${v.version}`,
                              message: `Downloads of ${v.version} fail. The version number is never used again.`,
                              impact: `go/modules/${mod.id}/versions`,
                              inUseName: v.version,
                              inUseLabel: "version",
                            })
                          }
                        >
                          Delete
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Section>
      {shown && <GoModView tenant={tenant} mod={mod} version={shown} />}
      {promoting && (
        <PromoteForm
          key={promoting.id}
          tenant={tenant}
          mod={mod}
          version={promoting}
          onDone={() => setPromoting(null)}
        />
      )}
      {grace.ui}
    </>
  );
}

function GoModView({ tenant, mod, version }: { tenant: TenantDetail; mod: GoModule; version: GoVersion }) {
  const { api } = useServices();
  const text = useLoad(
    () => api.getGoMod(tenant.slug, mod.id, version.id),
    [tenant.slug, mod.id, version.id],
  );
  return (
    <Section title={`go.mod of ${version.version}`}>
      <ErrorBanner error={text.error} />
      {text.loading && <p className="muted">Loading...</p>}
      {text.data !== null && text.data !== undefined && <CodeBlock title="go.mod" code={text.data} />}
    </Section>
  );
}

/** Promote: link the same zip into another namespace (ADR-0038 section 2, Q-G5). Storage counts once. */
function PromoteForm({
  tenant,
  mod,
  version,
  onDone,
}: {
  tenant: TenantDetail;
  mod: GoModule;
  version: GoVersion;
  onDone: () => void;
}) {
  const { api } = useServices();
  const nss = useLoad(() => api.listNamespaces(tenant.slug), [tenant.slug]);
  const others = (nss.data?.items ?? []).map((n) => n.slug).filter((s) => s !== mod.namespace);
  const [target, setTarget] = useState("");
  const [done, setDone] = useState<string | null>(null);
  const action = useAction();
  const ns = target || others[0] || "";

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!ns) return;
    await action.run(async () => {
      const r = await api.promoteGoVersion(tenant.slug, mod.id, version.id, ns);
      setDone(`Promoted ${r.path}@${r.version} to ${r.namespace}. It uses the same zip and the same h1.`);
    });
  }

  return (
    <Section title={`Promote ${version.version}`}>
      <p className="muted">
        The same zip goes into another namespace of this tenant. No copy is made, and storage counts it once.
      </p>
      <ErrorBanner error={nss.error ?? action.error} />
      {done ? (
        <p role="status">{done}</p>
      ) : others.length === 0 && nss.data ? (
        <p className="muted">This tenant has no other namespace.</p>
      ) : (
        <form className="form" onSubmit={onSubmit}>
          <label>
            To namespace
            <select value={ns} onChange={(e) => setTarget(e.target.value)}>
              {others.map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </label>
          <div className="row">
            <button type="submit" disabled={action.busy || !ns}>
              Promote to {ns || "namespace"}
            </button>
            <button type="button" className="secondary" onClick={onDone}>
              Cancel
            </button>
          </div>
        </form>
      )}
      {done && (
        <button type="button" className="secondary" onClick={onDone}>
          Close
        </button>
      )}
    </Section>
  );
}
